/**
 * Configuration types and helper functions for BuildHub project types, scopes, and stages.
 * Data is fetched from the backend via GraphQL (buildHubStageConfig query).
 *
 * Note: These types match the GraphQL schema and are used throughout the BuildHub creator flow.
 */

export type ProjectType = 'Residential' | 'Commercial' | 'Internal';

/**
 * Project scope option with metadata and associated stages
 */
interface ProjectScopeOption {
    value: string;
    label: string;
    emoji: string;
    description: string;
    stages: StageOption[];
    budgetOptions?: ProjectScopeBudgetOption[];
}

/**
 * Stage option
 */
interface StageOption {
    urlSegment: string;
    title: string;
    sortOrder: number;
}

/**
 * Budget option for a project scope returned by the backend.
 * @property key Unique identifier that matches the backend budget key.
 * @property label Human readable label for the budget range.
 * @property min Minimum budget amount represented by this option. Null when open ended. Undefined when serialized by Remix.
 * @property max Maximum budget amount represented by this option. Null when open ended. Undefined when serialized by Remix.
 */
interface ProjectScopeBudgetOption {
    key: string;
    label: string;
    min?: number | null;
    max?: number | null;
}

/**
 * Stage configuration structure returned from GraphQL
 */
export interface StageConfiguration {
    residential: ProjectScopeOption[];
    commercial: ProjectScopeOption[];
    internal: ProjectScopeOption[];
}

/**
 * Get project scope options based on project type from the stage configuration
 */
export function getScopesByProjectType(
    config: StageConfiguration,
    projectType: ProjectType
): ProjectScopeOption[] {
    switch (projectType) {
        case 'Residential':
            return config.residential;
        case 'Commercial':
            return config.commercial;
        case 'Internal':
            return config.internal;
        default:
            return [];
    }
}

/**
 * Get stages for a specific project type and scope from the stage configuration.
 * Searches by label (e.g., "Landscaping / Outdoor") since that's what we store in the database.
 */
export function getStagesForScope(
    config: StageConfiguration,
    projectType: ProjectType,
    projectScope: string
): StageOption[] {
    const scopes = getScopesByProjectType(config, projectType);
    const scope = scopes.find((s) => s.label === projectScope);
    return scope?.stages || [];
}
